import { motion } from 'framer-motion';
import { Zap, Settings, Cpu, Wrench, CheckCircle2, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const Services = () => {
  const services = [
    {
      icon: <Zap size={40} />,
      title: 'Hydro Power Plant Design',
      description: 'Complete electrical and control system design tailored to your project requirements. From feasibility studies to detailed engineering drawings.',
      features: [
        'Site assessment and turbine selection',
        'Electrical system design and single-line diagrams',
        'Control philosophy and logic design',
        'Protection coordination studies',
        'SCADA architecture design'
      ]
    },
    {
      icon: <Settings size={40} />,
      title: 'Control Panel Manufacturing',
      description: 'In-house fabrication of custom control panels built to international standards with precision and quality.',
      features: [
        'Main control panels',
        'Excitation and AVR panels',
        'Synchronization panels',
        'PLC and automation panels',
        'Auxiliary power distribution panels'
      ]
    },
    {
      icon: <Cpu size={40} />,
      title: 'Automation & SCADA',
      description: 'Advanced automation solutions with real-time monitoring, control, and data logging capabilities.',
      features: [
        'PLC programming and configuration',
        'HMI/SCADA development',
        'Remote monitoring systems',
        'IoT integration',
        'Data analytics and reporting'
      ]
    },
    {
      icon: <Wrench size={40} />,
      title: 'Installation & Commissioning',
      description: 'Complete turnkey solutions from equipment installation to final commissioning and handover.',
      features: [
        'Equipment installation supervision',
        'System integration and testing',
        'Factory Acceptance Testing (FAT)',
        'Site Acceptance Testing (SAT)',
        'Operator training'
      ]
    }
  ];

  const additionalServices = [
    {
      title: 'Governor Systems',
      description: 'Speed and load control systems for stable grid synchronization'
    },
    {
      title: 'Electronic Load Controllers',
      description: 'Frequency regulation through intelligent ballast load management'
    },
    {
      title: 'Retrofit & Upgrades',
      description: 'Modernization of existing control systems with latest technology'
    },
    {
      title: 'Maintenance Support',
      description: 'Annual maintenance contracts and spare parts supply'
    },
    {
      title: 'Technical Consulting',
      description: 'Expert guidance for hydro power projects at any stage'
    },
    {
      title: 'Training Services',
      description: 'Comprehensive operator and maintenance training programs'
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="services-page">
      <section className="py-24 bg-slate-950" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.h1
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="font-heading font-light text-5xl md:text-7xl tracking-tighter text-white mb-6 text-center"
            data-testid="services-title"
          >
            Our <span className="text-ocean-500">Services</span>
          </motion.h1>
          <motion.p
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto text-center"
          >
            Comprehensive solutions for hydro power control systems from concept to commissioning
          </motion.p>
        </div>
      </section>

      <section className="py-24 bg-slate-900" data-testid="core-services">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-white mb-16 text-center">
            Core Services
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                data-testid={`service-card-${index}`}
                className="bg-slate-900/50 border border-white/5 p-8 hover:border-ocean-500/30 transition-all"
              >
                <div className="text-ocean-500 mb-4">{service.icon}</div>
                <h3 className="font-heading text-2xl font-semibold text-white mb-4">{service.title}</h3>
                <p className="text-slate-400 leading-relaxed mb-6">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-slate-400 text-sm">
                      <CheckCircle2 size={16} className="mt-1 flex-shrink-0 text-ocean-500" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-950" data-testid="additional-services">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-white mb-16 text-center">
            Additional Services
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {additionalServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-slate-900/50 border border-white/5 p-6 hover:border-ocean-500/30 transition-all"
              >
                <h3 className="font-heading text-lg font-semibold text-white mb-3">{service.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-900" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-white mb-16 text-center">
            Our Process
          </h2>
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="flex gap-6 items-start">
              <div className="w-16 h-16 flex-shrink-0 border-2 border-ocean-500 flex items-center justify-center font-mono text-2xl text-ocean-500">
                01
              </div>
              <div>
                <h3 className="font-heading text-xl font-semibold text-white mb-2">
                  Consultation & Assessment
                </h3>
                <p className="text-slate-400 leading-relaxed">
                  Understanding your project requirements, site conditions, and technical specifications to develop the optimal solution.
                </p>
              </div>
            </div>

            <div className="flex gap-6 items-start">
              <div className="w-16 h-16 flex-shrink-0 border-2 border-ocean-500 flex items-center justify-center font-mono text-2xl text-ocean-500">
                02
              </div>
              <div>
                <h3 className="font-heading text-xl font-semibold text-white mb-2">
                  Design & Engineering
                </h3>
                <p className="text-slate-400 leading-relaxed">
                  Detailed engineering design with complete documentation, drawings, and specifications following international standards.
                </p>
              </div>
            </div>

            <div className="flex gap-6 items-start">
              <div className="w-16 h-16 flex-shrink-0 border-2 border-ocean-500 flex items-center justify-center font-mono text-2xl text-ocean-500">
                03
              </div>
              <div>
                <h3 className="font-heading text-xl font-semibold text-white mb-2">
                  Manufacturing & Testing
                </h3>
                <p className="text-slate-400 leading-relaxed">
                  Precision manufacturing of control panels with comprehensive Factory Acceptance Testing before shipment.
                </p>
              </div>
            </div>

            <div className="flex gap-6 items-start">
              <div className="w-16 h-16 flex-shrink-0 border-2 border-ocean-500 flex items-center justify-center font-mono text-2xl text-ocean-500">
                04
              </div>
              <div>
                <h3 className="font-heading text-xl font-semibold text-white mb-2">
                  Installation & Commissioning
                </h3>
                <p className="text-slate-400 leading-relaxed">
                  On-site installation, system integration, testing, and commissioning with comprehensive operator training.
                </p>
              </div>
            </div>

            <div className="flex gap-6 items-start">
              <div className="w-16 h-16 flex-shrink-0 border-2 border-ocean-500 flex items-center justify-center font-mono text-2xl text-ocean-500">
                05
              </div>
              <div>
                <h3 className="font-heading text-xl font-semibold text-white mb-2">
                  Support & Maintenance
                </h3>
                <p className="text-slate-400 leading-relaxed">
                  Ongoing technical support, maintenance services, and system upgrades throughout the project lifecycle.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        className="relative py-32 bg-cover bg-center"
        style={{
          backgroundImage:
            'url(https://images.unsplash.com/photo-1709131121922-028189a2c984?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxoeWRybyUyMHBvd2VyJTIwcGxhbnQlMjBkYW0lMjBzY2VuaWN8ZW58MHx8fHwxNzcwMjkyNTY1fDA&ixlib=rb-4.1.0&q=85)'
        }}
        data-testid="services-cta"
      >
        <div className="absolute inset-0 bg-slate-950/80" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-4xl md:text-6xl font-light tracking-tighter text-white mb-8">
            Ready to Start Your Project?
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Let our team of experts help you design and implement the perfect hydro power control solution.
          </p>
          <Link
            to="/contact"
            data-testid="services-contact-button"
            className="px-8 py-4 bg-ocean-500 hover:bg-ocean-600 text-white font-medium uppercase tracking-widest text-xs transition-all inline-flex items-center gap-2"
          >
            Get In Touch
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
